/**
 * Document 2 · Phase 9.3 — Admin AI Usage Dashboard.
 *
 * Admin-only page. Non-admins are redirected to /dashboard.
 *
 *   • 4 KPI tiles: Total Calls · Active Reps · Top Surface · Daily Avg
 *   • Daily volume line chart (Recharts) — one line per surface
 *   • Filters: date range (7d / 30d / 90d / custom) + surface + rep
 *   • Rep breakdown table
 *
 * Backed by GET /api/ai-usage/summary.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Sparkles, Users, TrendingUp, Activity, Loader2, RefreshCw, CalendarRange } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';


const SURFACE_COLORS = {
  vision_proposal: '#B89B5E',
  vision_proposal_autofill: '#D4A574',
  outreach_email: '#7C6A4D',
  call_clarity_brief: '#4A6F8C',
  signal_story: '#8B5E83',
};

const PRESETS = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];


function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}


function KpiTile({ label, value, hint, icon: Icon, testid }) {
  return (
    <Card className="rounded-sm border" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
            <div className="font-serif text-3xl md:text-4xl font-bold text-foreground leading-none">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
          </div>
          {Icon && <div className="rounded-sm p-2 bg-[#B89B5E]/10 text-[#B89B5E]"><Icon className="w-5 h-5" /></div>}
        </div>
      </CardContent>
    </Card>
  );
}


export default function AIUsageDashboard() {
  const { user } = useAuth();
  const api = useApi();

  const [startDate, setStartDate] = useState(isoDaysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [surface, setSurface] = useState('all');
  const [rep, setRep] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const isAdmin = user && (user.is_admin || user.role === 'admin');

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const params = { start: startDate, end: endDate };
      if (surface !== 'all') params.surface = surface;
      if (rep !== 'all') params.rep = rep;
      const res = await api.get('/ai-usage/summary', params);
      setData(res);
    } catch (e) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, surface, rep]);

  useEffect(() => {
    if (isAdmin) load();
  }, [load, isAdmin]);

  const applyPreset = (days) => {
    setStartDate(isoDaysAgo(days));
    setEndDate(today());
  };

  const surfaces = data?.surfaces || [];
  const labels = data?.surface_labels || {};

  const chartData = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((row) => {
      const out = { day: row.day.slice(5) }; // MM-DD
      surfaces.forEach((s) => { out[s] = row[s] || 0; });
      return out;
    });
  }, [data, surfaces]);

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="ai-usage-dashboard">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-[#B89B5E]" />
              <h1 className="font-serif text-3xl sm:text-4xl">AI Usage</h1>
              <Badge variant="outline" className="rounded-sm border-[#B89B5E]/50 text-[#B89B5E] text-[10px]">Admin</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Per-surface AI activity across the workspace. Read-only — cap changes live in Workspace Settings.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="rounded-sm"
            data-testid="ai-usage-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="rounded-sm border">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex items-center gap-2">
                <CalendarRange className="w-4 h-4 text-muted-foreground" />
                {PRESETS.map((p) => (
                  <Button
                    key={p.id}
                    type="button"
                    variant={endDate === today() && startDate === isoDaysAgo(p.days) ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-sm"
                    onClick={() => applyPreset(p.days)}
                    data-testid={`ai-usage-preset-${p.id}`}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-[11px] text-muted-foreground">Start</Label>
                  <Input
                    type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="rounded-sm h-9 w-[160px]" data-testid="ai-usage-start-date"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">End</Label>
                  <Input
                    type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="rounded-sm h-9 w-[160px]" data-testid="ai-usage-end-date"
                  />
                </div>
              </div>
              <div className="min-w-[220px]">
                <Label className="text-[11px] text-muted-foreground">Surface</Label>
                <Select value={surface} onValueChange={setSurface}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ai-usage-surface-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All surfaces</SelectItem>
                    {Object.keys(labels).length > 0 ? Object.entries(labels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    )) : null}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[220px]">
                <Label className="text-[11px] text-muted-foreground">Rep</Label>
                <Select value={rep} onValueChange={setRep}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ai-usage-rep-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All reps</SelectItem>
                    {(data?.by_rep || []).map((r) => (
                      <SelectItem key={r.user_id} value={r.user_id}>
                        {r.name || r.email || r.user_id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {err && (
          <div className="rounded-sm border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm" data-testid="ai-usage-error">
            {err}
          </div>
        )}

        {/* KPI Tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile
            label="Total AI Calls"
            value={loading ? '—' : (data?.totals?.total_calls ?? 0).toLocaleString()}
            hint={`Over ${data?.totals?.range_days ?? '—'} days`}
            icon={Activity}
            testid="ai-usage-kpi-total"
          />
          <KpiTile
            label="Active Reps"
            value={loading ? '—' : (data?.totals?.active_reps ?? 0).toLocaleString()}
            hint="Reps with at least 1 call in range"
            icon={Users}
            testid="ai-usage-kpi-reps"
          />
          <KpiTile
            label="Top Surface"
            value={loading ? '—' : (data?.totals?.top_surface_label || '—')}
            hint={data?.totals?.top_surface ? `${(data?.by_surface?.[data.totals.top_surface] ?? 0).toLocaleString()} calls` : 'No activity'}
            icon={TrendingUp}
            testid="ai-usage-kpi-top-surface"
          />
          <KpiTile
            label="Daily Avg"
            value={loading ? '—' : (data?.totals?.daily_avg ?? 0).toLocaleString()}
            hint="Calls per day across range"
            icon={Sparkles}
            testid="ai-usage-kpi-avg"
          />
        </div>

        {/* Line Chart */}
        <Card className="rounded-sm border" data-testid="ai-usage-chart">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold uppercase tracking-widest">Daily volume by surface</h3>
              {loading && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
            </div>
            <div className="w-full h-[320px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#888" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#888" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #ddd' }}
                      formatter={(value, name) => [value, labels[name] || name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => labels[v] || v} />
                    {surfaces.map((s) => (
                      <Line
                        key={s}
                        type="monotone"
                        dataKey={s}
                        stroke={SURFACE_COLORS[s] || '#888'}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                  No usage in selected range.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rep breakdown */}
        <Card className="rounded-sm border">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold uppercase tracking-widest mb-3">Usage by rep</h3>
            {data?.by_rep && data.by_rep.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ai-usage-rep-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                      <th className="py-2 pr-4">Rep</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 text-right">Total calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_rep.map((r) => (
                      <tr key={r.user_id} className="border-b last:border-b-0" data-testid={`ai-usage-rep-row-${r.user_id.slice(0, 8)}`}>
                        <td className="py-2.5 pr-4 font-medium">{r.name || '—'}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{r.email || '—'}</td>
                        <td className="py-2.5 text-right font-mono font-semibold" style={{ color: '#B89B5E' }}>
                          {r.total.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No rep activity in the selected range.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
